import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@heartplus/ui';
import { TOOLTIP_DELAY_MS } from '@heartplus/ui/tokens/tooltip-delays';
import * as React from 'react';

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

interface TooltipConfig {
  side: Side;
  align: Align;
  className?: string;
}

const TooltipConfigContext = React.createContext<TooltipConfig | undefined>(
  undefined,
);

interface BaseTooltipRootProps {
  side?: Side;
  align?: Align;
  className?: string;
  /**
   * Hover delay before the tooltip opens, in ms.
   *
   * Set here rather than by wrapping each tooltip in its own `TooltipProvider`:
   * a provider per tooltip means one extra component + context per instance
   * (a page with 25 of them paid for 25 providers), and Radix reads
   * `delayDuration` off the Root first, falling back to the provider. One
   * provider lives at the app root; per-tooltip timing lives here.
   *
   * Defaults to `relaxed`, the SAME value the app-root provider uses. That
   * alignment is the point: this once defaulted to 100 while the root sat at
   * 700, so omitting the prop looked like "no opinion" but silently made a
   * tooltip seven times slower depending on which path it took. With both ends
   * on one number, almost nothing should pass this at all — the one value worth
   * stating is `TOOLTIP_DELAY_MS.none`, where hover IS the interaction.
   */
  delayDuration?: number;
  /**
   * Controlled open state, for a caller that must own when the tooltip shows
   * (hover-only, or forced shut while its trigger is inert). Omit both to
   * stay uncontrolled.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

const BaseTooltipRoot: React.FC<BaseTooltipRootProps> = ({
  side = 'top',
  align = 'center',
  className,
  delayDuration = TOOLTIP_DELAY_MS.relaxed,
  open,
  onOpenChange,
  children,
}) => {
  const contextValue = React.useMemo(
    () => ({ side, align, className }),
    [side, align, className],
  );
  return (
    <Tooltip
      delayDuration={delayDuration}
      open={open}
      onOpenChange={onOpenChange}
    >
      <TooltipConfigContext.Provider value={contextValue}>
        {children}
      </TooltipConfigContext.Provider>
    </Tooltip>
  );
};

/**
 * Anything the browser already puts in the tab order. `:not([disabled])` is
 * load-bearing, not tidiness: the commonest tooltip shape here is a <span>
 * wrapping a DISABLED <Button> whose tooltip explains the block
 * (critical-summary/shared/transmission-action-button.tsx renders the tooltip
 * ONLY while the button is disabled). A disabled control is not focusable, so
 * counting it as one skips the tab stop in exactly the case WCAG 2.1.1 is
 * being cited for. `aria-disabled` controls stay focusable and still count —
 * that is the pattern SignOffGuardTooltip's callers use deliberately.
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"]):not([disabled])';

/**
 * A trigger that IS a disabled control cannot be given a tab stop — `tabindex`
 * does not override `disabled`. Adding one there would be dead markup and a
 * focus ring that can never render; such a call site has to wrap the control in
 * a span instead, which is also what restores hover (a disabled control emits
 * no pointer events either).
 *
 * The `fieldset[disabled]` clause names the form controls explicitly rather
 * than using `*`: a disabled fieldset disables its descendant CONTROLS, not
 * everything inside it, so a `<span>` or a link in there is still focusable
 * and still needs its tab stop. `*` would have withheld one — reopening the
 * very gap this constant exists to close.
 */
const UNFOCUSABLE_TRIGGER_SELECTOR =
  'button[disabled], input[disabled], select[disabled], textarea[disabled], fieldset[disabled] :is(button, input, select, textarea)';

const Trigger: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Radix types the trigger ref as HTMLButtonElement (its default element).
  // Under `asChild` the node is whatever the caller passed — usually a span —
  // but the only members read here (`matches`, `querySelector`) are Element's,
  // so the declared type is safe.
  const ref = React.useRef<HTMLButtonElement>(null);
  const [needsTabStop, setNeedsTabStop] = React.useState(false);

  /**
   * A tooltip's content has to be reachable without a mouse (WCAG 2.1.1), and
   * Radix opens on focus — but only if something here can actually BE focused.
   * `asChild` puts the trigger props on whatever the caller passed, and callers
   * pass a mix: a `<Button>` (already focusable), a plain `<span>` wrapping a
   * badge (focusable by nothing), or a `<span>` wrapping its own button.
   *
   * So decide from the DOM instead of adding a blanket `tabIndex`: when the
   * trigger is focusable itself, or contains something focusable, focus already
   * reaches it and React's bubbling `onFocus` opens the tooltip — adding a tab
   * stop there would only create a second, redundant one. Only a trigger with
   * nothing focusable in it needs one of its own.
   *
   * Checked once on mount, so a trigger whose control toggles `disabled` while
   * mounted keeps the mount-time answer. That is deliberate rather than an
   * oversight: at every caller that toggles (critical-summary-patient-header's
   * prev/next, pdf-report-action-bar, transmission-action-bar-archive-action)
   * the tooltip only repeats the trigger's own `aria-label`/`title`, so a stale
   * answer costs the keyboard user no information.
   *
   * Do NOT "fix" it by re-running the probe on every render. The tab stop this
   * effect adds is itself matched by `FOCUSABLE_SELECTOR`'s
   * `[tabindex]:not([tabindex="-1"])` clause, so the probe would read its own
   * output as input and oscillate true → false → true until React throws
   * "Maximum update depth exceeded". Every re-run shape (a dependency-less
   * layout effect, `focusin`, `pointerenter`) hits this identically unless it
   * first subtracts the stop it added.
   */
  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    setNeedsTabStop(
      !element.matches(UNFOCUSABLE_TRIGGER_SELECTOR) &&
        !element.matches(FOCUSABLE_SELECTOR) &&
        element.querySelector(FOCUSABLE_SELECTOR) === null,
    );
  }, []);

  return (
    <TooltipTrigger
      asChild
      ref={ref}
      // Only set when we added the tab stop ourselves: a caller's own focus
      // treatment stays untouched, and the ring never appears on a trigger that
      // cannot be focused.
      {...(needsTabStop
        ? {
            tabIndex: 0,
            className:
              'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          }
        : {})}
    >
      {children}
    </TooltipTrigger>
  );
};

const Content: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cfg = React.useContext(TooltipConfigContext);
  return (
    <TooltipContent
      side={cfg?.side ?? 'top'}
      align={cfg?.align ?? 'center'}
      className={cn(
        // `max-w-xs` is a DEFAULT, not a rule: nothing in the Radix chain caps a
        // tooltip's width, so content that never wraps grows without bound — an
        // archived badge's attribution measured 519px for one practitioner name
        // and 885px for a long one, against a 100px trigger. 20rem is the width
        // this repo already reaches for by hand at most call sites.
        // A caller's own `max-w-*` still wins: `cn` is twMerge, arbitrary values
        // share a conflict group with the scale, and the caller's class is last
        // here AND again inside libs/ui's TooltipContent.
        'flex flex-col items-start self-stretch px-3 py-2 rounded-lg bg-card max-w-xs',
        cfg?.className,
      )}
    >
      {children}
    </TooltipContent>
  );
};

export const BaseTooltip = Object.assign(BaseTooltipRoot, {
  Trigger,
  Content,
});
